import { SupportHL7Message, formatDate, JsonDiffReact } from '@enter/core';
import * as S from './claim-timeline-table.styles';
import React from 'react';

interface IHL7MessageDetailProps {
    hl7Message: SupportHL7Message;
    before?: SupportHL7Message;
}

const safeParseJson = (text: string): unknown | null => {
    try {
        return JSON.parse(text);
    } catch {
        return null;
    }
};

export const HL7MessageDetail: React.FunctionComponent<IHL7MessageDetailProps> = (
    props: IHL7MessageDetailProps,
): React.ReactElement => {
    const { hl7Message, before } = props;

    const { parsed: parsedAfter, formatted: afterMessage } = React.useMemo(() => {
        const raw = hl7Message.message || '';
        const parsed = safeParseJson(raw);
        const formatted = (parsed ? JSON.stringify(parsed, null, 2) : raw).replace(/\r|\n/g, '\n');
        return { parsed, formatted };
    }, [hl7Message.message]);

    const { parsed: parsedBefore, formatted: beforeMessage } = React.useMemo(() => {
        const raw = before?.message || '';
        const parsed = safeParseJson(raw);
        const formatted = (parsed ? JSON.stringify(parsed, null, 2) : raw).replace(/\r|\n/g, '\n');
        return { parsed, formatted };
    }, [before?.message]);

    const showDiff = !!before;

    const renderCreated = (): string => {
        if (!hl7Message.created) {
            return '—';
        }

        try {
            return formatDate(hl7Message.created, 'MMM dd, yyyy HH:mm');
        } catch (error) {
            return '—';
        }
    };

    return (
        <S.ExpandContainer>
            <S.ExpandTable>
                <S.ExpandTBody>
                    <S.ExpandTR>
                        <S.ExpandTH>ID</S.ExpandTH>
                        <S.ExpandTD>{hl7Message.id || '—'}</S.ExpandTD>
                    </S.ExpandTR>
                    <S.ExpandTR>
                        <S.ExpandTH>Type</S.ExpandTH>
                        <S.ExpandTD>{hl7Message.type || '—'}</S.ExpandTD>
                    </S.ExpandTR>
                    <S.ExpandTR>
                        <S.ExpandTH>Created</S.ExpandTH>
                        <S.ExpandTD>{renderCreated()}</S.ExpandTD>
                    </S.ExpandTR>
                    {!showDiff && (
                        <S.ExpandTR>
                            <S.ExpandTH>Payload</S.ExpandTH>
                            <S.ExpandTD>
                                <S.PayloadWrapper>
                                    <pre>{afterMessage ? afterMessage : '—'}</pre>
                                </S.PayloadWrapper>
                            </S.ExpandTD>
                        </S.ExpandTR>
                    )}
                    {showDiff && (
                        <S.ExpandTR>
                            <S.ExpandTD colSpan={2}>
                                <S.JsonViewersContainer>
                                    <S.JsonViewerColumn>
                                        <S.JsonViewerLabel>Before</S.JsonViewerLabel>
                                        <S.JsonViewerWrapper>
                                            <pre style={{ fontSize: '12px' }}>{beforeMessage ? beforeMessage : ''}</pre>
                                        </S.JsonViewerWrapper>
                                    </S.JsonViewerColumn>
                                    <S.JsonViewerColumn>
                                        <S.JsonViewerLabel>After</S.JsonViewerLabel>
                                        <S.JsonViewerWrapper>
                                            <pre style={{ fontSize: '12px' }}>{afterMessage ? afterMessage : ''}</pre>
                                        </S.JsonViewerWrapper>
                                    </S.JsonViewerColumn>
                                    <S.JsonViewerColumn>
                                        <S.JsonViewerLabel>Diff</S.JsonViewerLabel>
                                        <S.JsonViewerWrapper>
                                            <JsonDiffReact right={parsedAfter} left={parsedBefore} show={false} />
                                        </S.JsonViewerWrapper>
                                    </S.JsonViewerColumn>
                                </S.JsonViewersContainer>
                            </S.ExpandTD>
                        </S.ExpandTR>
                    )}
                </S.ExpandTBody>
            </S.ExpandTable>
        </S.ExpandContainer>
    );
};
